import { Incident, CatalogTask } from '@/types/dashboard';

interface TimeSeriesData {
  date: Date;
  value: number;
}

interface PredictionResult {
  nextWeekIncidents: number;
  confidence: number;
  trend: 'increasing' | 'decreasing' | 'stable';
}

interface PatternAnalysis {
  commonRootCauses: string[];
  recurringIssues: string[];
  impactAreas: string[];
}

interface RiskAssessment {
  riskLevel: 'High' | 'Medium' | 'Low';
  factors: string[];
  recommendations: string[];
}

export class MLService {
  private static instance: MLService;

  private constructor() {}

  public static getInstance(): MLService {
    if (!MLService.instance) {
      MLService.instance = new MLService();
    }
    return MLService.instance;
  }

  // Time series analysis for incident prediction with time period consideration
  private analyzeTimeSeries(data: TimeSeriesData[], timePeriodDays: number = 30): PredictionResult {
    // Safety check for NaN or invalid time period
    if (isNaN(timePeriodDays) || timePeriodDays <= 0) {
      timePeriodDays = 30; // Default fallback
    }
    
    console.log('MLService - Analyzing with time period:', timePeriodDays, 'days');
    console.log('MLService - Time series data:', data.length, 'points');
    
    // Handle insufficient data with intelligent fallbacks
    if (data.length === 0) {
      console.warn('MLService - No time series data available, using fallback');
      return {
        nextWeekIncidents: timePeriodDays <= 30 ? 1 : timePeriodDays <= 60 ? 2 : 3,
        confidence: 0.1,
        trend: 'stable'
      };
    }
    
    if (data.length === 1) {
      console.warn('MLService - Only one data point available, using simple projection');
      const singleValue = data[0].value || 1;
      const timePeriodMultiplier = timePeriodDays <= 30 ? 1.0 : 
                                  timePeriodDays <= 60 ? 1.1 : 1.2;
      return {
        nextWeekIncidents: Math.max(1, Math.round(singleValue * timePeriodMultiplier)),
        confidence: 0.2,
        trend: 'stable'
      };
    }

    // Calculate moving average with safety checks
    const windowSize = Math.min(7, data.length); // Adjust window size if we have less data
    const movingAverages = [];
    
    for (let i = windowSize - 1; i < data.length; i++) {
      const window = data.slice(i - windowSize + 1, i + 1);
      const validValues = window.filter(item => item && !isNaN(item.value) && isFinite(item.value));
      
      if (validValues.length === 0) {
        console.warn('MLService - No valid values in window, using fallback');
        movingAverages.push(1); // Fallback value
        continue;
      }
      
      const sum = validValues.reduce((acc, curr) => acc + curr.value, 0);
      const average = sum / validValues.length;
      movingAverages.push(isNaN(average) ? 1 : average);
    }
    
    console.log('MLService - Moving averages calculated:', movingAverages.length, 'values');

    // Calculate trend
    const recentValues = movingAverages.slice(-Math.min(14, movingAverages.length));
    const trend = this.calculateTrend(recentValues);
    console.log('MLService - Trend calculated:', trend);

    // Predict next week with some fluctuations and time period adjustments
    const lastValue = movingAverages[movingAverages.length - 1];
    console.log('MLService - Last value for prediction:', lastValue);
    
    // Safety checks for NaN values
    if (isNaN(lastValue) || lastValue < 0) {
      console.warn('Invalid lastValue in prediction:', lastValue);
      return {
        nextWeekIncidents: Math.max(1, Math.round(data.length / 7)), // Fallback: average daily incidents
        confidence: 0.3,
        trend: 'stable'
      };
    }
    
    const basePrediction = lastValue * (1 + (isNaN(trend) ? 0 : trend));
    
    // Time period adjustment: longer periods often reveal higher baseline activity
    // 30 days = baseline, 60 days = +10%, 90+ days = +20%
    const timePeriodMultiplier = timePeriodDays <= 30 ? 1.0 : 
                                timePeriodDays <= 60 ? 1.1 : 1.2;
    
    const adjustedPrediction = basePrediction * timePeriodMultiplier;
    
    // Add realistic fluctuations based on historical variance
    const variance = this.calculateVariance(recentValues);
    const safeVariance = isNaN(variance) || variance < 0 ? 1 : variance;
    const fluctuation = (Math.random() - 0.5) * 0.3 * Math.sqrt(safeVariance);
    const prediction = Math.round(Math.max(1, adjustedPrediction + fluctuation));

    // Calculate confidence based on data consistency AND time period
    // Longer time periods = more data = higher confidence
    const dataPoints = data.length;
    const minDataPoints = 7; // Minimum 7 days of data
    const optimalDataPoints = timePeriodDays * 0.8; // 80% of time period is optimal
    
    // Base confidence from data consistency
    const baseConfidence = variance > 0 ? Math.max(0, Math.min(1, 1 - (variance / lastValue))) : 0.5;
    
    // Time period bonus: more data = higher confidence
    const timePeriodBonus = Math.min(0.4, (dataPoints - minDataPoints) / (optimalDataPoints - minDataPoints) * 0.4);
    
    // Sample size confidence: more data points = more confidence
    const sampleSizeBonus = Math.min(0.2, dataPoints / 50 * 0.2);
    
    const confidence = Math.max(0.1, Math.min(0.95, baseConfidence + timePeriodBonus + sampleSizeBonus));

    // Final safety check to ensure no NaN values are returned
    const finalPrediction = isNaN(prediction) || prediction < 0 ? 
      (timePeriodDays <= 30 ? 1 : timePeriodDays <= 60 ? 2 : 3) : 
      Math.max(1, Math.round(prediction));
    
    const finalConfidence = isNaN(confidence) ? 0.3 : Math.max(0.1, Math.min(0.95, confidence));
    
    console.log('MLService - Final result:', {
      nextWeekIncidents: finalPrediction,
      confidence: finalConfidence,
      trend: trend > 0.1 ? 'increasing' : trend < -0.1 ? 'decreasing' : 'stable'
    });

    return {
      nextWeekIncidents: finalPrediction,
      confidence: finalConfidence,
      trend: trend > 0.1 ? 'increasing' : trend < -0.1 ? 'decreasing' : 'stable'
    };
  }

  private calculateTrend(values: number[]): number {
    if (values.length < 2) return 0;
    
    // Filter out NaN and invalid values
    const validValues = values.filter(v => !isNaN(v) && isFinite(v));
    if (validValues.length < 2) return 0;
    
    const n = validValues.length;
    let sumX = 0, sumY = 0, sumXY = 0, sumXX = 0;
    
    validValues.forEach((y, x) => {
      sumX += x;
      sumY += y;
      sumXY += x * y;
      sumXX += x * x;
    });

    const denominator = (n * sumXX) - (sumX * sumX);
    if (denominator === 0) return 0; // Avoid division by zero
    
    const trend = ((n * sumXY) - (sumX * sumY)) / denominator;
    return isNaN(trend) ? 0 : trend;
  }

  private calculateVariance(values: number[]): number {
    if (values.length < 2) return 1; // Return 1 instead of 0 to avoid division issues
    
    // Filter out NaN and invalid values
    const validValues = values.filter(v => !isNaN(v) && isFinite(v));
    if (validValues.length < 2) return 1;
    
    const mean = validValues.reduce((a, b) => a + b) / validValues.length;
    if (isNaN(mean)) return 1;
    
    const variance = validValues.reduce((a, b) => a + Math.pow(b - mean, 2), 0) / validValues.length;
    return isNaN(variance) || variance < 0 ? 1 : variance;
  }

  // Pattern analysis for incidents
  private analyzePatterns(incidents: Incident[]): PatternAnalysis {
    const descriptions = incidents.map(inc => inc["Short description"].toLowerCase());
    
    // Extract common words and phrases
    const wordFrequency = new Map<string, number>();
    descriptions.forEach(desc => {
      const words = desc.split(/\W+/).filter(w => w.length > 3);
      words.forEach(word => {
        wordFrequency.set(word, (wordFrequency.get(word) || 0) + 1);
      });
    });

    // Sort by frequency
    const sortedWords = Array.from(wordFrequency.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([word]) => word);

    // Group incidents by type
    const types = new Set<string>();
    descriptions.forEach(desc => {
      if (desc.includes('error')) types.add('Error');
      if (desc.includes('timeout')) types.add('Timeout');
      if (desc.includes('fail')) types.add('Failure');
      if (desc.includes('crash')) types.add('Crash');
    });

    return {
      commonRootCauses: sortedWords.slice(0, 5),
      recurringIssues: Array.from(types),
      impactAreas: this.identifyImpactAreas(incidents)
    };
  }

  private identifyImpactAreas(incidents: Incident[]): string[] {
    const areas = new Set<string>();
    incidents.forEach(inc => {
      if (inc.Service) areas.add(inc.Service);
      // Use "Assigned to" field since Assignment_group doesn't exist in the type
      if (inc["Assigned to"]) areas.add(inc["Assigned to"]);
    });
    return Array.from(areas);
  }

  // Risk assessment
  private assessRisk(incidents: Incident[], catalogTasks: CatalogTask[]): RiskAssessment {
    const recentIncidents = incidents.filter(inc => {
      if (!inc.Opened) return false;
      
      const date = new Date(inc.Opened);
      if (isNaN(date.getTime())) return false;
      
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      return date >= thirtyDaysAgo;
    });

    const highPriorityIncidents = recentIncidents.filter(inc => 
      ['1', '2', 'high', 'critical'].includes(inc.Priority.toLowerCase())
    );

    const riskFactors = [];
    if (highPriorityIncidents.length > 5) riskFactors.push('High number of critical incidents');
    if (recentIncidents.length > 20) riskFactors.push('High incident volume');
    
    const riskLevel = this.calculateRiskLevel(riskFactors.length);
    const recommendations = this.generateRecommendations(riskLevel, riskFactors);

    return {
      riskLevel,
      factors: riskFactors,
      recommendations
    };
  }

  private calculateRiskLevel(factorCount: number): 'High' | 'Medium' | 'Low' {
    if (factorCount >= 2) return 'High';
    if (factorCount === 1) return 'Medium';
    return 'Low';
  }

  private generateRecommendations(riskLevel: string, factors: string[]): string[] {
    const recommendations = [];
    
    if (riskLevel === 'High') {
      recommendations.push('Immediate review of critical systems required');
      recommendations.push('Consider implementing additional monitoring');
      recommendations.push('Schedule emergency maintenance window');
    } else if (riskLevel === 'Medium') {
      recommendations.push('Review system logs for potential issues');
      recommendations.push('Update monitoring thresholds');
      recommendations.push('Plan maintenance window');
    } else {
      recommendations.push('Continue regular monitoring');
      recommendations.push('Review and update documentation');
    }

    return recommendations;
  }

  // Public methods
  public async analyzeIncidents(incidents: Incident[], catalogTasks: CatalogTask[], timePeriodDays: number = 30): Promise<{
    predictions: PredictionResult;
    patterns: PatternAnalysis;
    riskAssessment: RiskAssessment;
  }> {
    console.log('MLService - Starting analysis with:', {
      incidentsCount: incidents.length,
      catalogTasksCount: catalogTasks.length,
      timePeriodDays
    });
    
    // Prepare time series data
    const timeSeriesData = this.prepareTimeSeriesData(incidents);
    console.log('MLService - Time series data prepared:', {
      dataPoints: timeSeriesData.length,
      sampleData: timeSeriesData.slice(0, 3)
    });
    
    // Perform analysis
    const predictions = this.analyzeTimeSeries(timeSeriesData, timePeriodDays);
    console.log('MLService - Predictions generated:', predictions);
    
    const patterns = this.analyzePatterns(incidents);
    const riskAssessment = this.assessRisk(incidents, catalogTasks);

    return {
      predictions,
      patterns,
      riskAssessment
    };
  }

  private prepareTimeSeriesData(incidents: Incident[]): TimeSeriesData[] {
    const dailyCounts = new Map<string, number>();
    
    incidents.forEach(inc => {
      // Validate and parse the date
      if (!inc.Opened) return;
      
      const date = new Date(inc.Opened);
      
      // Check if the date is valid
      if (isNaN(date.getTime())) {
        console.warn('Invalid date found in incident:', inc.Opened);
        return;
      }
      
      try {
        const dateStr = date.toISOString().split('T')[0];
        dailyCounts.set(dateStr, (dailyCounts.get(dateStr) || 0) + 1);
      } catch (error) {
        console.warn('Error converting date to ISO string:', inc.Opened, error);
        return;
      }
    });

    return Array.from(dailyCounts.entries())
      .map(([dateStr, count]) => ({
        date: new Date(dateStr),
        value: count
      }))
      .filter(item => !isNaN(item.date.getTime()))
      .sort((a, b) => a.date.getTime() - b.date.getTime());
  }
} 